import type { App } from 'vue';
import { useUserStore } from '@/stores/user';
import {
  isAimetaAndroidWebView,
  getWebViewHostObjects,
  getWebView2HostObjects,
  getAimetaNativeBridge,
} from '@/utils/platform';
import { parseAimetaPlatformBridgeResponse } from '@/utils/aimetaPlatformBridge';

/** 开播前 CosyVoice Hub 同步进度（与 JsManager.GetCosyVoiceHubSyncProgress 一致） */
export interface CosyVoiceHubSyncProgress {
  taskId: string;
  status: string;
  totalVoices: number;
  currentVoiceIndex: number;
  currentPronouncer: string;
  overallPercent: number;
  hubPhasePercent: number;
  message: string;
  speedText: string;
}

export interface BatchDownloadProgress {
  taskId: string; // 任务ID
  status: string; // 任务状态
  totalCount: number; // 原始文件列表总数
  needDownloadCount: number; // 实际需要下载的文件数（去除已存在的）
  completedCount: number; // 已成功下载完成的文件数
  currentIndex: number; // 当前正在下载的文件序号（从1开始）
  currentUrl: string; // 当前正在下载的文件URL
  currentFileTotalBytes: number; // 当前文件总字节数
  currentFileDownloadedBytes: number; // 当前文件已下载字节数
  currentFileProgressPercent: number; // 当前文件下载进度百分比（0~100）
  currentFileSpeedBytesPerSecond: number; // 当前文件下载速度（字节/秒）
}

const CHECKPOINT_BOOL_KEYS = [
  'product',
  'robot',
  'scheduledShutdown',
  'washManuscript',
  'promotionScript',
  'timedGuidance',
  'knowledgeAllowImportWord',
] as const;

/** 多段话术单段字数：字典可配置上限（与后台 system_param 一致） */
export const FREE_SCRIPT_CHARS_HARD_MAX = 1000;

const parseCheckpointBoolValue = (raw: unknown): boolean | undefined => {
  let v: unknown = raw;
  const parsed = parseMaybeJson(v);
  if (parsed !== v) {
    v = parsed;
  }
  if (v && typeof v === 'object' && !Array.isArray(v) && 'value' in (v as Record<string, unknown>)) {
    v = (v as Record<string, unknown>).value;
  }
  const b = parseMaybeBool(v);
  return b === true || b === false ? b : undefined;
};

/** checkpoint：知识库是否允许导入 Word，未配置时默认 true */
export function parseKnowledgeAllowImportWord(value: unknown): boolean {
  const b = parseCheckpointBoolValue(value);
  return b === undefined ? true : b;
};

/** 与 main.ts defaultAppConfig.scriptSettings 一致，用于合并/兜底 */
const SCRIPT_SETTINGS_DEFAULT = { freedom: true, fixed: true };

const parseMaybeJson = (value: any) => {
  if (typeof value !== 'string') return value;
  const text = value.trim();
  if (!text) return value;
  if (
    (text.startsWith('{') && text.endsWith('}')) ||
    (text.startsWith('[') && text.endsWith(']'))
  ) {
    try {
      return JSON.parse(text);
    } catch {
      return value;
    }
  }
  return value;
};

const parseMaybeBool = (value: any) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const lower = value.trim().toLowerCase();
    if (lower === 'true' || lower === '1') return true;
    if (lower === 'false' || lower === '0') return false;
  }
  return value;
};

const normalizeCheckpointConfig = (raw: any) => {
  const data = raw && typeof raw === 'object' ? { ...raw } : {};

  // scriptSettings：须为普通对象；与默认合并；子项须为明确布尔，否则删键以免 undefined 覆盖默认
  if (Object.prototype.hasOwnProperty.call(data, 'scriptSettings')) {
    const parsed = parseMaybeJson(data.scriptSettings);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const merged = { ...SCRIPT_SETTINGS_DEFAULT, ...parsed };
      (['freedom', 'fixed'] as const).forEach((sk) => {
        if (!Object.prototype.hasOwnProperty.call(merged, sk)) return;
        const bv = parseMaybeBool((merged as any)[sk]);
        if (bv === true || bv === false) {
          (merged as any)[sk] = bv;
        } else {
          delete (merged as any)[sk];
        }
      });
      data.scriptSettings = { ...SCRIPT_SETTINGS_DEFAULT, ...merged };
    } else {
      delete data.scriptSettings;
    }
  }

  // liveType：非空数组，或与 scriptSettings 相同的 { code: bool }，取值为 true 的键作为平台列表
  if (Object.prototype.hasOwnProperty.call(data, 'liveType')) {
    const parsed = parseMaybeJson(data.liveType);
    if (Array.isArray(parsed) && parsed.length > 0) {
      data.liveType = parsed;
    } else if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const codes = Object.keys(parsed).filter(
        (k) => parseMaybeBool((parsed as any)[k]) === true
      );
      if (codes.length > 0) {
        data.liveType = codes;
      } else {
        delete data.liveType;
      }
    } else {
      delete data.liveType;
    }
  }

  // speechEngines：非空数组，或与 liveType 相同的 { engineCode: bool }，true 的键作为引擎列表
  if (Object.prototype.hasOwnProperty.call(data, 'speechEngines')) {
    const parsed = parseMaybeJson(data.speechEngines);
    if (Array.isArray(parsed) && parsed.length > 0) {
      data.speechEngines = parsed;
    } else if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const codes = Object.keys(parsed).filter(
        (k) => parseMaybeBool((parsed as any)[k]) === true
      );
      if (codes.length > 0) {
        data.speechEngines = codes;
      } else {
        delete data.speechEngines;
      }
    } else {
      delete data.speechEngines;
    }
  }

  // defaultLanguage / skin：非空字符串才保留
  (['defaultLanguage', 'skin'] as const).forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(data, key)) return;
    const v = data[key];
    if (typeof v !== 'string' || !v.trim()) {
      delete data[key];
    }
  });

  // accountMode：键名大小写不敏感（如 AccountMode）；值为 email/phone；兼容 {"value":"email"} 等 JSON
  {
    const keys = Object.keys(data);
    const modeKeys = keys.filter((k) => k.toLowerCase() === 'accountmode');
    let rawMode: unknown;
    if (modeKeys.length > 0) {
      rawMode = data[modeKeys[0]];
      modeKeys.forEach((k) => {
        delete data[k];
      });
    }
    if (rawMode !== undefined && rawMode !== null) {
      let parsed = parseMaybeJson(rawMode);
      if (
        parsed &&
        typeof parsed === 'object' &&
        !Array.isArray(parsed) &&
        Object.prototype.hasOwnProperty.call(parsed, 'value')
      ) {
        rawMode = (parsed as Record<string, unknown>).value;
        parsed = parseMaybeJson(rawMode);
      }
      if (typeof parsed === 'string') {
        rawMode = parsed;
      }
    }
    const am = String(rawMode ?? '')
      .trim()
      .toLowerCase();
    if (am === 'email' || am === 'phone') {
      data.accountMode = am;
    }
  }

  // freeScriptMinChars：1–FREE_SCRIPT_CHARS_HARD_MAX 整数
  if (Object.prototype.hasOwnProperty.call(data, 'freeScriptMinChars')) {
    const n = parseInt(String(data.freeScriptMinChars).trim(), 10);
    if (Number.isFinite(n) && n >= 1 && n <= FREE_SCRIPT_CHARS_HARD_MAX) {
      data.freeScriptMinChars = n;
    } else {
      delete data.freeScriptMinChars;
    }
  }
  // freeScriptMaxChars：1–FREE_SCRIPT_CHARS_HARD_MAX 整数（多段话术单段最大字数）
  if (Object.prototype.hasOwnProperty.call(data, 'freeScriptMaxChars')) {
    const n = parseInt(String(data.freeScriptMaxChars).trim(), 10);
    if (Number.isFinite(n) && n >= 1 && n <= FREE_SCRIPT_CHARS_HARD_MAX) {
      data.freeScriptMaxChars = n;
    } else {
      delete data.freeScriptMaxChars;
    }
  }

  // 仅当 checkpoint 原文包含该键且能解析为明确布尔值时才写入，避免 undefined/'' 覆盖 main.ts 里的默认值
  CHECKPOINT_BOOL_KEYS.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(data, key)) {
      return;
    }
    const v = parseCheckpointBoolValue(data[key]);
    if (v === true || v === false) {
      data[key] = v;
    } else {
      delete data[key];
    }
  });
  return data;
};

/**
 * 合并 checkpoint 与 defaultAppConfig：去掉 undefined，避免展开覆盖默认项；数组/嵌套对象再兜底。
 */
export function mergeAppConfigWithDefaults(
  defaults: Record<string, any>,
  partial: Record<string, any>
): Record<string, any> {
  const clean: Record<string, any> = {};
  Object.keys(partial).forEach((k) => {
    if (partial[k] !== undefined) {
      clean[k] = partial[k];
    }
  });
  const out = { ...defaults, ...clean };

  const dss = defaults.scriptSettings;
  const oss = out.scriptSettings;
  if (oss && typeof oss === 'object' && !Array.isArray(oss)) {
    out.scriptSettings = {
      ...(typeof dss === 'object' && dss && !Array.isArray(dss)
        ? dss
        : SCRIPT_SETTINGS_DEFAULT),
      ...oss,
    };
  }

  let ltOut = out.liveType;
  if (ltOut && typeof ltOut === 'object' && !Array.isArray(ltOut)) {
    const codes = Object.keys(ltOut).filter(
      (k) => parseMaybeBool((ltOut as any)[k]) === true
    );
    ltOut = codes.length > 0 ? codes : null;
    out.liveType = ltOut;
  }
  if (!Array.isArray(out.liveType) || out.liveType.length === 0) {
    out.liveType = defaults.liveType;
  }

  let seOut = out.speechEngines;
  if (seOut && typeof seOut === 'object' && !Array.isArray(seOut)) {
    const codes = Object.keys(seOut).filter(
      (k) => parseMaybeBool((seOut as any)[k]) === true
    );
    seOut = codes.length > 0 ? codes : null;
    out.speechEngines = seOut;
  }
  if (!Array.isArray(out.speechEngines) || out.speechEngines.length === 0) {
    out.speechEngines = defaults.speechEngines;
  }

  const fm = out.freeScriptMinChars;
  if (
    typeof fm !== 'number' ||
    !Number.isFinite(fm) ||
    fm < 1 ||
    fm > FREE_SCRIPT_CHARS_HARD_MAX
  ) {
    out.freeScriptMinChars =
      typeof defaults.freeScriptMinChars === 'number'
        ? defaults.freeScriptMinChars
        : 30;
  }
  const fmx = out.freeScriptMaxChars;
  if (
    typeof fmx !== 'number' ||
    !Number.isFinite(fmx) ||
    fmx < 1 ||
    fmx > FREE_SCRIPT_CHARS_HARD_MAX
  ) {
    out.freeScriptMaxChars =
      typeof defaults.freeScriptMaxChars === 'number'
        ? defaults.freeScriptMaxChars
        : 300;
  }

  return out;
}

/**
 * speechEngines / liveType：checkpoint 可为 { code: boolean }，合并后一般为 string[]。
 * 列表过滤请用本函数，以兼容数组与对象两种形态。
 */
export function isAppConfigCodeEnabled(
  mapOrList: unknown,
  code: string
): boolean {
  if (mapOrList == null || !code) return false;
  if (Array.isArray(mapOrList)) return mapOrList.includes(code);
  if (typeof mapOrList === 'object') {
    return (
      parseMaybeBool((mapOrList as Record<string, unknown>)[code]) === true
    );
  }
  return false;
}

/**
 * 直播间设置窗体 Hide 时 WebView 与 Vue 通常不卸载，但关播后 JsManager 等宿主对象可能已不可用。
 * 须在隐藏窗口的瞬间掐断 JS 侧对宿主的轮询/异步调用，避免出现 0x80020006（DISP_E_UNKNOWNNAME）。
 * 再次打开设置会 Navigate，模块会重新执行；若同会话复用同一页，由 liveRoomSetting onMounted 置回 false。
 */
let liveRoomSettingsHostSuspended = false;

export function setLiveRoomSettingsHostSuspended(suspended: boolean) {
  liveRoomSettingsHostSuspended = suspended;
}

const methods = {
  async callAimetaPlatform(method: string, platform = 'wechat_video', ...args: any[]) {
    const host = (getWebView2HostObjects() as any)?.AimetaPlatform as any;
    if (!host || typeof host[method] !== 'function') {
      return { rspCode: 1, rspMsg: 'AimetaPlatform host unavailable' };
    }
    const raw = await host[method](platform, ...args);
    return parseAimetaPlatformBridgeResponse(raw);
  },
  async getAimetaPlatformStatus(platform = 'wechat_video') {
    return await this.callAimetaPlatform('GetPlatformStatus', platform);
  },
  async openAimetaPlatformAccessibility(platform = 'wechat_video') {
    return await this.callAimetaPlatform('OpenAccessibilitySettings', platform);
  },
  async warmUpAimetaPlatform(platform = 'wechat_video') {
    return await this.callAimetaPlatform('WarmUp', platform);
  },
  async getAimetaPlatformLoginQr(platform = 'wechat_video') {
    return await this.callAimetaPlatform('GetLoginQr', platform);
  },
  async refreshAimetaPlatformLoginQr(platform = 'wechat_video') {
    return await this.callAimetaPlatform('RefreshLoginQr', platform);
  },
  async getAimetaPlatformSession(platform = 'wechat_video') {
    return await this.callAimetaPlatform('GetSession', platform);
  },
  /** 快速探测视频号登录态（缓存优先，未登录时短 probe） */
  async probeAimetaPlatformLoginSession(platform = 'wechat_video') {
    return await this.callAimetaPlatform('ProbeLoginSession', platform);
  },
  async clearAimetaPlatformSession(platform = 'wechat_video') {
    return await this.callAimetaPlatform('ClearPlatformSession', platform);
  },
  /** 仅拉直播状态（打开面板时用，不做全量 RPC） */
  async getAimetaPlatformLiveStatus(platform = 'wechat_video') {
    const status = await this.callAimetaPlatform('GetLiveStatus', platform);
    return { rspCode: 0, data: { status } };
  },
  async startAimetaPlatformEventStream(platform = 'wechat_video') {
    return await this.callAimetaPlatform('StartEventStream', platform);
  },
  async stopAimetaPlatformEventStream(platform = 'wechat_video') {
    return await this.callAimetaPlatform('StopEventStream', platform);
  },
  async collapseAimetaFloatingPanel(platform = 'wechat_video') {
    return await this.callAimetaPlatform('CollapseFloatingPanel', platform);
  },
  async getAimetaPlatformEventStreamState(platform = 'wechat_video') {
    return await this.callAimetaPlatform('GetEventStreamState', platform);
  },
  async getAimetaPlatformCapabilities(platform = 'wechat_video') {
    return await this.callAimetaPlatform('GetCapabilities', platform);
  },
  async setAimetaPlatformLiveContext(
    payload: Record<string, unknown>,
    platform = 'wechat_video'
  ) {
    return await this.callAimetaPlatform('SetLiveContext', platform, payload);
  },
  async sendAimetaPlatformLiveText(text: string, platform = 'wechat_video') {
    return await this.callAimetaPlatform('SendLiveText', platform, text);
  },
  async closeAimetaPlatformLive(platform = 'wechat_video') {
    return await this.callAimetaPlatform('CloseLive', platform);
  },
  //获取logo
  async getSystemLogo() {
    if (getWebView2HostObjects()?.GetOEMPngPath) {
      const result =
        await getWebView2HostObjects()!.GetOEMPngPath.GetOEMPngPath();
      return result;
    }
  },
  //获取系统宽高
  async getSystemScreen() {
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      let result =
        await getWebView2HostObjects()!.CallsStartupVoice.GetPrimaryScreen();
      result = JSON.parse(result);
      return result;
    }
    if (typeof window !== 'undefined') {
      return { width: window.innerWidth || 0, height: window.innerHeight || 0 };
    }
    return { width: 0, height: 0 };
  },
  async getDeviceInfo(): Promise<{ deviceCode: string; deviceName: string }> {
    if (isAimetaAndroidWebView()) {
      const code = String(window.__AIMETA_DEVICE_CODE || '').trim();
      const name = String(window.__AIMETA_DEVICE_NAME || '').trim();
      if (code) {
        return { deviceCode: code, deviceName: name || code };
      }
    }
    try {
      const host = getWebViewHostObjects()?.GetDeviceCode as
        | { GetDeviceCode?: () => Promise<string> | string }
        | undefined;
      if (host?.GetDeviceCode) {
        const raw = await Promise.resolve(host.GetDeviceCode());
        const parsed =
          typeof raw === 'string' ? JSON.parse(raw || '{}') : (raw as any);
        const deviceCode = String(parsed?.deviceCode || '').trim();
        const deviceName = String(parsed?.deviceName || '').trim();
        if (deviceCode) {
          return {
            deviceCode,
            deviceName: deviceName || deviceCode,
          };
        }
      }
    } catch {}
    try {
      const bridge = getAimetaNativeBridge() as any;
      if (bridge) {
        if (typeof bridge.getDeviceInfo === 'function') {
          const raw = bridge.getDeviceInfo();
          const parsed =
            typeof raw === 'string' ? JSON.parse(raw || '{}') : (raw as any);
          const deviceCode = String(parsed?.deviceCode || '').trim();
          const deviceName = String(parsed?.deviceName || '').trim();
          if (deviceCode) {
            return {
              deviceCode,
              deviceName: deviceName || deviceCode,
            };
          }
        }
        const codeRaw =
          typeof bridge.getDeviceCode === 'function' ? bridge.getDeviceCode() : '';
        const nameRaw =
          typeof bridge.getDeviceName === 'function' ? bridge.getDeviceName() : '';
        const deviceCode = String(codeRaw || '').trim();
        const deviceName = String(nameRaw || '').trim();
        if (deviceCode) {
          return {
            deviceCode,
            deviceName: deviceName || deviceCode,
          };
        }
      }
    } catch {}
    return { deviceCode: 'web', deviceName: 'web' };
  },
  //打开直播间设置弹窗
  async openOrCloseLiveForm(isOpen: boolean, url: string) {
    if (getWebView2HostObjects()?.CallsWindowInterface) {
      const result =
        await getWebView2HostObjects()!.CallsWindowInterface.OpenOrCloseLiveForm(
          isOpen,
          url
        );
      return result;
    }
  },
  //打开知识库弹窗
  async openOrCloseKnowledgeForm(isOpen: boolean, url: string) {
    if (getWebView2HostObjects()?.CallsWindowInterface) {
      const result =
        await getWebView2HostObjects()!.CallsWindowInterface.OpenOrCloseLiveKnowledgeBase(
          isOpen,
          url
        );
      // console.log(result, "打开知识库返回值");
      return result;
    }
  },
  //显示直播间弹窗
  async showLiveForm() {
    if (getWebView2HostObjects()?.CallsWindowInterface) {
      await getWebView2HostObjects()!.CallsWindowInterface.ShowLiveForm();
    }
  },
  async selectFile(type: string) {
    if (getWebView2HostObjects()?.RoleAndVoice) {
      const result =
        await getWebView2HostObjects()!.RoleAndVoice.GetFilePath(type);
      return result;
    }
  },
  //本地图片路径转base64文件
  async fileToBase64(path: string) {
    type MimeTypes = {
      [key in
      | '.mp4'
      | '.webm'
      | '.ogg'
      | '.mp3'
      | '.wav'
      | '.png'
      | '.jpg'
      | '.jpeg'
      | '.gif'
      | '.svg'
      | '.pdf'
      | '.json']: string;
    };
    const mimeTypes = {
      // 视频
      '.mp4': 'video/mp4',
      '.webm': 'video/webm',
      '.ogg': 'video/ogg',
      // 音频
      '.mp3': 'audio/mpeg',
      '.wav': 'audio/wav',
      // 图片
      '.png': 'image/png',
      '.jpg': 'image/jpeg',
      '.jpeg': 'image/jpeg',
      '.gif': 'image/gif',
      '.svg': 'image/svg+xml',
      // 其他
      '.pdf': 'application/pdf',
      '.json': 'application/json',
    };
    const voiceHost = getWebView2HostObjects()?.CallsStartupVoice as
      | { GetFileByte?: (p: string) => Promise<string> }
      | undefined;
    if (voiceHost && typeof voiceHost.GetFileByte === 'function' && path) {
      const result = await voiceHost.GetFileByte(path);
      if (!result) return null;
      const extension = path
        .slice(path.lastIndexOf('.') >>> 0)
        .toLowerCase() as keyof MimeTypes;
      const mimeType = mimeTypes[extension] || 'application/octet-stream';
      const base64 = `data:${mimeType};base64,${result}`;
      return base64;
    }
  },
  //根据文件夹路径获取文件列表
  async pathToFileList(path: string) {
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      let result =
        await getWebView2HostObjects()!.CallsStartupVoice.StartInitializationForm(
          path
        );
      result = JSON.parse(result);
      return result;
    }
  },
  /**
   * 下载网络文件到本地
   * @param {Object} data - 下载的网络地址
   * @param {string} data.downloadUrl - 下载的网络地址
   * @param {string} data.downloadPathName - 文件名称（时间戳）
   * @param {number} data.downloadType - 下载类型 1: 角色2:语音3:背景
   * @returns {Promise<string>}
   */
  async downloadFilesLocally(data: any) {
    if (getWebView2HostObjects()?.WebDownloadFile) {
      const params = JSON.stringify(data);
      const result =
        await getWebView2HostObjects()!.WebDownloadFile.DownloadFilesLocally(
          params
        );
      return result;
    }
  },

  /**
   * 将网络图片下载到客户端 Data\Cache（downloadType=4），无浏览器 CORS 限制。
   * 成功后返回本地绝对路径，供 fileToBase64 等读取。
   */
  async downloadImageToDataCache(
    downloadUrl: string,
    fileName: string,
    timeoutMs = 120000
  ): Promise<string | null> {
    const host = getWebView2HostObjects()?.WebDownloadFile;
    if (!host?.DownloadFilesLocally || !host?.GetDownloadProgress) {
      return null;
    }
    const params = JSON.stringify({
      downloadUrl,
      downloadPathName: fileName,
      downloadType: 4,
    });
    await host.DownloadFilesLocally(params);
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const prog = await host.GetDownloadProgress(downloadUrl);
      if (prog) {
        try {
          const j = JSON.parse(prog as string);
          const status = j?.data?.status;
          const title = j?.data?.title;
          if (
            status === 'success' &&
            title &&
            typeof title === 'string' &&
            !title.includes('失败')
          ) {
            return title;
          }
          if (status === 'error') {
            return null;
          }
        } catch {
          /* ignore */
        }
      }
      await new Promise((r) => setTimeout(r, 200));
    }
    return null;
  },
  //下载文件进度
  async downloadFilesProgress(path = '') {
    if (liveRoomSettingsHostSuspended) {
      return JSON.stringify({ data: { progress: 0, status: 'error' } });
    }
    if (getWebView2HostObjects()?.WebDownloadFile) {
      return await getWebView2HostObjects()!.WebDownloadFile.GetDownloadProgress(
        path
      );
    }
  },
  //生成角色(下载角色到本地)
  async generateRole(data: any) {
    if (getWebView2HostObjects()?.RoleAndVoice) {
      // 将对象转换为JSON字符串
      const params = JSON.stringify(data);
      let result =
        await getWebView2HostObjects()!.RoleAndVoice.AddOrModifyRole(
          params
        );
      result = JSON.parse(result);
      return result;
    }
  },
  //生成角色进度
  async getGenerateProgress() {
    if (getWebView2HostObjects()?.RoleAndVoice) {
      const result =
        await getWebView2HostObjects()!.RoleAndVoice.GetCurrentProgress();
      // console.log('生成角色进度:', result);
      return result;
    }
  },
  //生成语音(下载语音到本地)
  async generateVoice(data: any) {
    if (getWebView2HostObjects()?.RoleAndVoice) {
      // 将对象转换为JSON字符串
      const params = JSON.stringify(data);
      let result =
        await getWebView2HostObjects()!.RoleAndVoice.AddOrModifyVoice(
          params
        );
      result = JSON.parse(result);
      return result;
    }
  },
  /** 专业语音：仅同步 Hub 模型到本地语音目录并更新 config.json（如仅编辑保存未走生成流程） */
  async syncCosyVoiceHubModel(data: {
    pronouncer: string;
    cosyvoiceModelHubRef: string;
  }) {
    if (getWebView2HostObjects()?.RoleAndVoice) {
      const params = JSON.stringify(data);
      const raw =
        await getWebView2HostObjects()!.RoleAndVoice.SyncCosyVoiceHubModel(
          params
        );
      return JSON.parse(raw as string);
    }
  },
  // 变更多语言
  async changeLanguage(language: string) {
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      const result =
        await getWebView2HostObjects()!.CallsStartupVoice.ChangeLanguage(
          language
        );
      return result;
    }
  },
  // 获取当前使用的语言环境
  async getLanguage() {
    const voiceHost = getWebView2HostObjects()?.CallsStartupVoice as
      | { GetLanguage?: () => Promise<string> }
      | undefined;
    if (voiceHost && typeof voiceHost.GetLanguage === 'function') {
      return voiceHost.GetLanguage();
    }
    if (isAimetaAndroidWebView()) {
      const lang = String((window as any).__AIMETA_LANG || '').trim();
      return lang || 'zh';
    }
  },

  // 批量下载文件（自由话术音频）
  async batchDownloadFiles(downloads: { url: string; directory: string }[]) {
    if (getWebView2HostObjects()?.JsManager) {
      const params = JSON.stringify({ downloads });
      console.log('批量下载参数：', params);
      const result =
        await getWebView2HostObjects()!.JsManager.BatchDownloadFilesLocally(
          params
        );
      return JSON.parse(result);
    }
  },
  // 获取批量下载进度
  async getBatchDownloadProgress(taskId: string) {
    if (liveRoomSettingsHostSuspended) return undefined;
    if (getWebView2HostObjects()?.JsManager) {
      return await getWebView2HostObjects()!.JsManager.GetBatchDownloadProgress(
        taskId
      );
    }
  },

  /** 开播前 / 生成语音：CosyVoice Hub 同步。开播 Pro 可设 skipHubSyncForLiveStart 跳过仓库比对与下载。 */
  async startCosyVoiceHubSyncForLive(
    voices: {
      pronouncer: string;
      cosyvoiceModelHubRef: string;
      /** CosyVoice spk / prompt_text，与声音名称一致 */
      promptText?: string;
    }[],
    options?: { skipHubSyncForLiveStart?: boolean }
  ) {
    const host = getWebView2HostObjects()?.JsManager;
    if (!host?.StartCosyVoiceHubSyncForLive) {
      return {
        code: 1,
        message: 'JsManager.StartCosyVoiceHubSyncForLive unavailable',
      };
    }
    const payload: Record<string, unknown> = { voices };
    if (options?.skipHubSyncForLiveStart === true) {
      payload.skipHubSyncForLiveStart = true;
    }
    const raw = await host.StartCosyVoiceHubSyncForLive(
      JSON.stringify(payload)
    );
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  },

  async getCosyVoiceHubSyncProgress(taskId: string) {
    const host = getWebView2HostObjects()?.JsManager;
    if (!host?.GetCosyVoiceHubSyncProgress) {
      return { code: 1, message: 'GetCosyVoiceHubSyncProgress unavailable' };
    }
    try {
      // 同步 string 与 Task/Promise 两种宿主桥接都兼容，避免 await 永不 settle 导致轮询卡死
      const raw = await Promise.resolve(
        host.GetCosyVoiceHubSyncProgress(taskId)
      );
      if (typeof raw === 'string') {
        return JSON.parse(raw);
      }
      if (raw && typeof raw === 'object' && 'code' in (raw as object)) {
        return raw as { code: number; message?: string; data?: unknown };
      }
      const s = String(raw ?? '').trim();
      if (s.startsWith('{')) {
        return JSON.parse(s);
      }
    } catch (e) {
      return { code: 1, message: String(e) };
    }
    return { code: 1, message: 'Invalid CosyVoice hub progress response' };
  },

  // 复制文件到本地指定文件夹: fileContent 文件Base64编码
  async copyFileToLocalDir(
    fileContent: string,
    targetDirectory: string,
    targetFileName: string
  ) {
    const params = JSON.stringify({
      fileContent,
      targetDirectory,
      targetFileName,
    });
    if (getWebView2HostObjects()?.JsManager?.CopyFileToDataDirectory) {
      return await getWebView2HostObjects()!.JsManager.CopyFileToDataDirectory(
        params
      );
    }
  },

  //本地启动直播服务（开始直播）
  async startServer(data: any, isLocal = true, isPreview = false) {
    console.log('调用本地方法开始直播:', data);
    const params = JSON.stringify(data);
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      return await getWebView2HostObjects()!.CallsStartupVoice.StartServer(
        params,
        isLocal,
        isPreview
      );
    }
    return { rspCode: 0, rspMsg: 'success' };
  },
  //远程直播服务获取IP地址（开始直播）
  async getServerAddress() {
    if (getWebView2HostObjects()?.JsManager) {
      return await getWebView2HostObjects()!.JsManager.getSchedulerAddress();
    }
  },

  //本地结束直播服务（结束直播）
  async stopServer() {
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      const result =
        await getWebView2HostObjects()!.CallsStartupVoice.StopServer();
      return result;
    }
    return { rspCode: 0, rspMsg: 'success' };
  },

  /** Android 云算力：取消 TTS 排队（开播前） */
  async cancelCloudLiveQueue() {
    if (getWebView2HostObjects()?.CallsStartupVoice?.CancelCloudLiveQueue) {
      return await getWebView2HostObjects()!.CallsStartupVoice.CancelCloudLiveQueue();
    }
    return { rspCode: 0, rspMsg: 'success' };
  },

  //启动直播进度
  async getCreateLiveRoomProgress() {
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      const result =
        await getWebView2HostObjects()!.CallsStartupVoice.GetStartServicProgress();
      return result;
    }
    return JSON.stringify({
      rspCode: 0,
      rspMsg: 'success',
      data: {
        progress: 100,
        status: 'success',
        title: '启动完成',
        step: 1,
        total_step: 1,
      },
    });
  },

  /** Android 云算力：开播前探测是否可复用磁盘（二次开播 0–20%） */
  async getCloudLiveReuseHint(payload: Record<string, unknown> | string) {
    const params =
      typeof payload === 'string' ? payload : JSON.stringify(payload);
    const host = getWebView2HostObjects()?.CallsStartupVoice;
    if (host?.GetCloudLiveReuseHint) {
      const raw = await host.GetCloudLiveReuseHint(params);
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    }
    console.warn(
      '[CloudLive] GetCloudLiveReuseHint 未注册（请更新 App 内 webview2_host_shim）'
    );
    return {
      rspCode: 0,
      data: { eligible: false, show_choice: false, showChoice: false },
    };
  },

  /** Android 云算力：调度队列与当前播放调试快照 */
  async getCloudLiveDebugStatus() {
    if (getWebView2HostObjects()?.CallsStartupVoice?.GetCloudLiveDebugStatus) {
      return await getWebView2HostObjects()!.CallsStartupVoice.GetCloudLiveDebugStatus();
    }
    return JSON.stringify({
      rspCode: 0,
      rspMsg: 'success',
      data: { active: false },
    });
  },

  /** Android：本机是否有可恢复的直播（关 App 后） */
  async getLiveRecoveryPending() {
    if (getWebView2HostObjects()?.CallsStartupVoice?.GetLiveRecoveryPending) {
      const raw =
        await getWebView2HostObjects()!.CallsStartupVoice.GetLiveRecoveryPending();
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    }
    return { rspCode: 0, data: { pending: false } };
  },

  async resumeLiveRecovery() {
    if (getWebView2HostObjects()?.CallsStartupVoice?.ResumeLiveRecovery) {
      const raw =
        await getWebView2HostObjects()!.CallsStartupVoice.ResumeLiveRecovery();
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    }
    return { rspCode: 0, rspMsg: 'success' };
  },

  async dismissLiveRecovery() {
    if (getWebView2HostObjects()?.CallsStartupVoice?.DismissLiveRecovery) {
      const raw =
        await getWebView2HostObjects()!.CallsStartupVoice.DismissLiveRecovery();
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    }
    return { rspCode: 0, rspMsg: 'success' };
  },
  //直播间开始监听
  async liveRoomListen(data: object) {
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      const params = JSON.stringify(data);
      const result =
        await getWebView2HostObjects()!.CallsStartupVoice.StartSpider(
          params
        );
      return result;
    }
  },
  //直播间结束监听
  async stopLiveRoomListen() {
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      const result =
        await getWebView2HostObjects()!.CallsStartupVoice.StopSpider();
      return result;
    }
  },
  // pino: 仅通知调度器建立 spider 连接；本地 pino_spider 在 startServer（开播）时由宿主拉起
  async startSpiderListenOnly(data: any = { data: {} }) {
    const payload = JSON.stringify(data || { data: {} });
    if (getWebView2HostObjects()?.JsManager?.StartSpiderListen) {
      return await getWebView2HostObjects()!.JsManager.StartSpiderListen(
        payload
      );
    }
    if (
      getWebView2HostObjects()!.CallsStartupVoice?.StartSpiderListen
    ) {
      return await getWebView2HostObjects()!.CallsStartupVoice.StartSpiderListen(
        payload
      );
    }
    if (
      getWebView2HostObjects()!.CallsStartupVoice
        ?.CallTheSchedulerInterface
    ) {
      return await getWebView2HostObjects()!.CallsStartupVoice.CallTheSchedulerInterface(
        payload,
        'api/live_room/start_spider_listen'
      );
    }
    return JSON.stringify({
      code: 1,
      message: 'StartSpiderListen host object unavailable',
    });
  },
  // pino: 仅通知调度器关闭 spider 连接
  async stopSpiderListenOnly(data: any = { data: {} }) {
    const payload = JSON.stringify(data || { data: {} });
    if (getWebView2HostObjects()?.JsManager?.StopSpiderListen) {
      return await getWebView2HostObjects()!.JsManager.StopSpiderListen(
        payload
      );
    }
    if (getWebView2HostObjects()?.CallsStartupVoice?.StopSpiderListen) {
      return await getWebView2HostObjects()!.CallsStartupVoice.StopSpiderListen(
        payload
      );
    }
    if (
      getWebView2HostObjects()!.CallsStartupVoice
        ?.CallTheSchedulerInterface
    ) {
      return await getWebView2HostObjects()!.CallsStartupVoice.CallTheSchedulerInterface(
        payload,
        'api/live_room/stop_spider_listen'
      );
    }
    return JSON.stringify({
      code: 1,
      message: 'StopSpiderListen host object unavailable',
    });
  },
  /**
   * 删除本地文件
   * @param {Object} data - 参数
   * @param {string} data.deleteCategory - 删除类型1:目录 2:文件
   * @param {string} data.deletePathName - 文件夹名或文件名
   * @param {number} data.deleteType - 删除类型 1:角色2:语音3:背景
   * @returns {Promise<string>}
   */
  async deleteLocalFile(data: object) {
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      const params = JSON.stringify(data);
      let result =
        await getWebView2HostObjects()!.CallsStartupVoice.DeleteFile(
          params
        );
      result = JSON.parse(result);
      return result;
    }
  },
  //对比本地角色和服务器角色
  async compareWithLocalRole(data: object) {
    if (getWebView2HostObjects()?.RoleAndVoice) {
      // 将对象转换为JSON字符串
      const params = JSON.stringify(data);
      const result =
        await getWebView2HostObjects()!.RoleAndVoice.CompareWithLocalRole(
          params
        );
      return result;
    }
    return JSON.stringify({ code: 0, message: '' });
  },
  // 使用 C# 宿主 + ffmpeg 执行抠像，返回本地输出路径
  async chromaKeyByFfmpeg(data: {
    sourceUrl: string;
    outputFileName?: string;
    similarity?: number;
    smoothness?: number;
    keyColor?: string;
    despillMix?: number;
    despillExpand?: number;
  }) {
    if (getWebView2HostObjects()?.RoleAndVoice) {
      const params = JSON.stringify(data);
      const result =
        await getWebView2HostObjects()!.RoleAndVoice.ChromaKeyByFfmpeg(
          params
        );
      return result;
    }
    return null;
  },
  /** 抠像结果已读入内存后删除宿主 Data\\Cache 下临时文件（路径须落在 Cache 目录内） */
  async deleteChromaCacheFile(absolutePath: string) {
    if (
      !absolutePath ||
      !getWebView2HostObjects()!.RoleAndVoice?.DeleteChromaCacheFile
    ) {
      return;
    }
    try {
      const params = JSON.stringify({ path: absolutePath });
      await getWebView2HostObjects()!.RoleAndVoice.DeleteChromaCacheFile(
        params
      );
    } catch (e) {
      console.warn('[chroma] deleteChromaCacheFile', e);
    }
  },
  //对比本地语音和服务器语音
  async compareWithLocalVoice(data: object) {
    if (getWebView2HostObjects()?.RoleAndVoice) {
      const params = JSON.stringify(data);
      const result =
        await getWebView2HostObjects()!.RoleAndVoice.CompareWithLocalVoice(
          params
        );
      return result;
    }
    return JSON.stringify({ code: 0, message: '' });
  },
  //选择文件夹
  async selectFolder() {
    if (getWebView2HostObjects()?.RoleAndVoice) {
      const result =
        await getWebView2HostObjects()!.RoleAndVoice.GetDescriptionPath();
      if (result == '') return;
      // console.log('文件目录地址:', result);
      return result;
    }
  },

  //获取直播监听登录二维码
  async getLiveBroadcastCode() {
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      const result =
        await getWebView2HostObjects()!.CallsStartupVoice.GetQRCodeAddress();
      // console.log('二维码地址：', result);
      return result;
    }
  },

  //抖音直播监听退出登录
  async douyinLogout() {
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      let result =
        await getWebView2HostObjects()!.CallsStartupVoice.LogOutSpider();
      result = JSON.parse(result);
      // console.log('退出登录', result);
      return result;
    }
  },

  //关闭直播间设置页面（需完整收尾时，如确认关播后）
  async closeLiveRoomSettingsPage() {
    if (isAimetaAndroidWebView()) {
      liveRoomSettingsHostSuspended = false;
      return;
    }
    liveRoomSettingsHostSuspended = true;
    if (getWebView2HostObjects()?.CallsWindowInterface) {
      await getWebView2HostObjects()!.CallsWindowInterface.CloseProgram();
    }
  },

  /** 仅隐藏直播间设置窗口，不停止直播、不执行 CloseProgram（不关调度/Player） */
  async hideLiveRoomSettingsWindow() {
    // Android App 为单 WebView 路由，勿沿用 Windows 壳「隐藏窗体 + 挂起宿主」逻辑
    if (isAimetaAndroidWebView()) {
      liveRoomSettingsHostSuspended = false;
      return;
    }
    liveRoomSettingsHostSuspended = true;
    const host = getWebView2HostObjects()?.CallsWindowInterface as {
      HideLiveRoomSettingsOnly?: () => void | Promise<void>;
    } | undefined;
    if (host?.HideLiveRoomSettingsOnly) {
      await host.HideLiveRoomSettingsOnly();
    } else {
      await this.closeLiveRoomSettingsPage();
    }
  },

  //刷新主页面
  async refreshWebView() {
    if (getWebView2HostObjects()?.CallsWindowInterface) {
      //刷新主页面
      getWebView2HostObjects()!.CallsWindowInterface.SafeRefreshWebView();
    }
  },

  //传token给本地
  async localSetToken(token = '') {
    console.log('token', token);
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      await getWebView2HostObjects()!.CallsStartupVoice.SetToken(token);
    }
  },

  //启动调度器服务
  async startSchedulerServer(data: any) {
    const params = JSON.stringify(data);
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      let result =
        await getWebView2HostObjects()!.CallsStartupVoice.StartSchedulerServer(
          params
        );
      result = JSON.parse(result);
      // console.log('启动返回：', result);
      return result;
    }
    return { rspCode: 0, rspMsg: 'success' };
  },

  async sendBarrageTestServer(data: any) {
    const params = JSON.stringify({ data: data });
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      let result =
        await getWebView2HostObjects()!.CallsStartupVoice.SendBarrageTest(
          params
        );
      result = JSON.parse(result);
      return result;
    }
  },

  //关闭调度器服务
  async closeSchedulerServer(data: any) {
    const params = JSON.stringify(data);
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      let result =
        await getWebView2HostObjects()!.CallsStartupVoice.CloseSchedulerServer(
          params
        );
      result = JSON.parse(result);
      // console.log('关闭返回：', result);
      return result;
    }
    return { rspCode: 0, rspMsg: 'success' };
  },

  //人工回复
  async schedulerManualReply(data: any) {
    const params = JSON.stringify(data);
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      let result =
        await getWebView2HostObjects()!.CallsStartupVoice.SchedulerManualReply(
          params
        );
      result = JSON.parse(result);
      // console.log('人工回复返回：', result);
      return result;
    }
  },

  /** 自由话术直播：从首段文案重新播报（调调度器） */
  async schedulerRestartFreeScriptBroadcast(data: any) {
    const params = JSON.stringify(data ?? {});
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      const result =
        await getWebView2HostObjects()!.CallsStartupVoice.SchedulerRestartFreeScriptBroadcast(
          params
        );
      return JSON.parse(result);
    }
  },

  //更新配置
  async schedulerUpdateConfig(data: any) {
    const params = JSON.stringify(data);
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      let result =
        await getWebView2HostObjects()!.CallsStartupVoice.SchedulerUpdateConfig(
          params
        );
      result = JSON.parse(result);
      // console.log('更新配置返回：', result);
      return result;
    }
    return { rspCode: 1, rspMsg: 'host unavailable' };
  },

  //获取版本号
  async getCurrentVersion() {
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      const result =
        await getWebView2HostObjects()!.CallsStartupVoice.GetCurrentVersionName();
      // console.log('版本名:', result);
      return result;
    }
  },

  //获取cookie
  async getCookie(url: string) {
    // console.log('url', url);
    if (getWebView2HostObjects()?.CallsStartupVoice) {
      const result =
        await getWebView2HostObjects()!.CallsStartupVoice.GetCookie(
          url
        );
      // console.log('启动返回:', result);
      return result;
    }
  },

  //打开高级功能
  async openADvanced(id: any, type: number) {
    // console.log('开启高级功能传参:id,type', id, type);
    if (getWebView2HostObjects()?.CallsWindowInterface) {
      const result =
        await getWebView2HostObjects()!.CallsWindowInterface.PopUpAWinFrom(
          type,
          id
        );
      // console.log('打开高级功能:', result);
      return result;
    }
  },

  //调度器转发接口
  // api示例: api/live_room/get_position_list
  async callInterface(data: any = {}, api: string) {
    if (liveRoomSettingsHostSuspended) {
      return { rspCode: 1, rspMsg: 'live room settings hidden', data: null };
    }
    if (!getWebView2HostObjects()?.CallsStartupVoice) {
      return { rspCode: 1, rspMsg: 'host unavailable', data: null };
    }
    const userStore = useUserStore();
    const params = {
      token: userStore.token,
      deviceCode: userStore.deviceCode,
      roleId: 0,
      data: data,
    };
    const paramsJson = JSON.stringify(params);
    if (
      getWebView2HostObjects()!.CallsStartupVoice?.CallTheSchedulerInterface
    ) {
      const result =
        await getWebView2HostObjects()!.CallsStartupVoice.CallTheSchedulerInterface(
          paramsJson,
          api
        );
      return JSON.parse(result || '');
    }
    return { rspCode: 1, rspMsg: 'host unavailable', data: null };
  },

  /**
   * 当前场次弹幕回复日志：经 JsManager → 调度器，数据来自调度器本地 META_DIR/Data/Cache（勿传 sessionId）。
   * 历史场次仍用 getReplyLogListApi 直连 Config。
   */
  async getReplyLogListThroughScheduler(data: any = {}) {
    if (liveRoomSettingsHostSuspended) {
      return {
        rspCode: 1,
        rspMsg: 'live room settings hidden',
        data: [],
        total: 0,
      };
    }
    const userStore = useUserStore();
    const params = {
      token: userStore.token,
      deviceCode: userStore.deviceCode,
      roleId: 0,
      data,
    };
    const paramsJson = JSON.stringify(params);
    const jm = getWebView2HostObjects()?.JsManager as any;
    if (
      jm &&
      (typeof jm.callSchedulerReplyLogList === 'function' ||
        typeof jm.CallSchedulerReplyLogList === 'function')
    ) {
      const fn =
        typeof jm.callSchedulerReplyLogList === 'function'
          ? jm.callSchedulerReplyLogList.bind(jm)
          : jm.CallSchedulerReplyLogList.bind(jm);
      console.log(
        '[ReplyLogTrace] getReplyLogListThroughScheduler → JsManager.callSchedulerReplyLogList'
      );
      const result = await fn(paramsJson);
      const parsed =
        typeof result === 'string' ? JSON.parse(result || '{}') : result;
      console.log('[ReplyLogTrace] scheduler reply_log_list rsp', {
        rspCode: parsed?.rspCode,
        total: parsed?.total,
        dataLen: Array.isArray(parsed?.data) ? parsed.data.length : null,
      });
      return parsed;
    }
    if (
      getWebView2HostObjects()!.CallsStartupVoice
        ?.CallTheSchedulerInterface
    ) {
      console.log(
        '[ReplyLogTrace] getReplyLogListThroughScheduler → CallsStartupVoice.CallTheSchedulerInterface(reply_log_list)'
      );
      const result =
        await getWebView2HostObjects()!.CallsStartupVoice.CallTheSchedulerInterface(
          paramsJson,
          'api/live_room/reply_log_list'
        );
      const parsed = JSON.parse(result || '{}');
      console.log('[ReplyLogTrace] scheduler reply_log_list rsp', {
        rspCode: parsed?.rspCode,
        total: parsed?.total,
        dataLen: Array.isArray(parsed?.data) ? parsed.data.length : null,
      });
      return parsed;
    }
    console.warn(
      '[ReplyLogTrace] getReplyLogListThroughScheduler: host unavailable (无 JsManager / CallsStartupVoice)'
    );
    return { rspCode: 1, rspMsg: 'host unavailable', data: [], total: 0 };
  },

  /** 调度器进程内是否已 start 本场（GET getStatus；VO 为 SnakeCase → data.live_started） */
  async getSchedulerLiveStarted(): Promise<boolean> {
    if (liveRoomSettingsHostSuspended) return false;
    const trim = (s: string, n = 400) =>
      s.length <= n ? s : `${s.slice(0, n)}…`;

    const readLiveStarted = (data: unknown): boolean => {
      if (!data || typeof data !== 'object') return false;
      const d = data as Record<string, unknown>;
      if (d.liveStarted === true) return true;
      if (d.live_started === true) return true;
      return false;
    };

    const parse = (
      raw: unknown,
      source: string
    ): { ok: boolean; liveStarted: boolean } => {
      try {
        const text = typeof raw === 'string' ? raw : JSON.stringify(raw);
        const j = typeof raw === 'string' ? JSON.parse(raw || '{}') : raw || {};
        const liveStarted = readLiveStarted(j?.data);
        console.log('[ReplyLogTrace] getSchedulerLiveStarted parse', {
          source,
          liveStarted,
          rspCode: j?.rspCode,
          rawPreview: typeof text === 'string' ? trim(text) : text,
        });
        return { ok: true, liveStarted };
      } catch (e) {
        console.warn('[ReplyLogTrace] getSchedulerLiveStarted JSON 解析失败', {
          source,
          e,
        });
        return { ok: false, liveStarted: false };
      }
    };

    const jsManager = getWebView2HostObjects()?.JsManager as any;
    const jmFn =
      jsManager &&
      (typeof jsManager.getSchedulerLiveStatusJson === 'function'
        ? jsManager.getSchedulerLiveStatusJson.bind(jsManager)
        : typeof jsManager.GetSchedulerLiveStatusJson === 'function'
          ? jsManager.GetSchedulerLiveStatusJson.bind(jsManager)
          : null);
    if (jmFn) {
      try {
        const raw = await jmFn();
        const { liveStarted } = parse(
          raw,
          'JsManager.getSchedulerLiveStatusJson'
        );
        return liveStarted;
      } catch (e) {
        console.warn(
          '[ReplyLogTrace] getSchedulerLiveStarted JsManager 调用异常',
          e
        );
      }
    } else {
      console.log(
        '[ReplyLogTrace] getSchedulerLiveStarted: JsManager 无 getSchedulerLiveStatusJson'
      );
    }

    const voice = getWebView2HostObjects()?.CallsStartupVoice as any;
    if (voice && typeof voice.GetSchedulerLiveStatusJson === 'function') {
      try {
        const raw = await voice.GetSchedulerLiveStatusJson();
        const { liveStarted } = parse(
          raw,
          'CallsStartupVoice.GetSchedulerLiveStatusJson'
        );
        return liveStarted;
      } catch (e) {
        console.warn(
          '[ReplyLogTrace] getSchedulerLiveStarted CallsStartupVoice(Get) 异常',
          e
        );
      }
    }
    if (voice && typeof voice.getSchedulerLiveStatusJson === 'function') {
      try {
        const raw = await voice.getSchedulerLiveStatusJson();
        const { liveStarted } = parse(
          raw,
          'CallsStartupVoice.getSchedulerLiveStatusJson'
        );
        return liveStarted;
      } catch (e) {
        console.warn(
          '[ReplyLogTrace] getSchedulerLiveStarted CallsStartupVoice(get) 异常',
          e
        );
      }
    }

    console.warn(
      '[ReplyLogTrace] getSchedulerLiveStarted: 未找到可用宿主方法，返回 false → 将走 Config reply_log/list'
    );
    return false;
  },

  // 获取机器人节点
  async getRobotNodeList(data: any = {}) {
    return await this.callInterface(data, 'api/live_room/get_position_list');
  },

  // 获取产品列表
  async getProductList(data: any = {}) {
    return await this.callInterface(
      data,
      'api/live_room_interaction/product_list'
    );
  },

  // 定时关播
  async setCloseTime(data: any = {}) {
    return await this.callInterface(data, 'api/live_room/set_close_time');
  },

  // 更新配置new
  /**
   * 下载网络文件到本地
   * @param {Object} data - 参数
   * @param {number} data.id - 直播间id
   * @param {string} data.module - 模块名 //SINGLE_ARTICLE-单篇话术 KEYWORD_ROBOT_POSITION-机器人指令
   * @returns {Promise<string>}
   */
  async updateConfigModule(data: any = {}) {
    return await this.callInterface(data, 'api/live_room/update_config_v2');
  },

  // 选择商品播报
  async selectGoodsBroadcast(data: any = {}) {
    return await this.callInterface(
      data,
      'api/live_room/select_goods_broadcast'
    );
  },

  // 获取当前播报的商品
  async getGoodsBroadcastNow(data: any = {}) {
    return await this.callInterface(data, 'api/live_room/goods_broadcast_now');
  },
  // 获取本地语言包
  async getLocalLanguage(lang: string) {
    // 1. Android App：通过 onPageFinished 注入的全局变量（最可靠）
    try {
      const injected = (window as any).__AIMETA_LANG_PACK__;
      if (injected && typeof injected === 'object' && Object.keys(injected).length > 0) {
        return injected;
      }
    } catch { }
    // 2. PC 桌面端：WebView2 bridge
    try {
      const jsManager = getWebView2HostObjects()?.JsManager;
      if (jsManager && typeof jsManager.getLanguageJson === 'function') {
        const result = await jsManager.getLanguageJson(lang);
        return JSON.parse(result ?? '{}');
      }
    } catch (e) {
      console.warn('getLocalLanguage (WebView2) error:', e);
    }
    // 3. Android App：AimetaNative bridge (备用)
    try {
      const bridge = getAimetaNativeBridge();
      if (bridge && typeof bridge.getLocalLanguage === 'function') {
        const result = bridge.getLocalLanguage(lang);
        if (result && typeof result === 'string') {
          return JSON.parse(result);
        }
      }
    } catch (e) {
      console.warn('getLocalLanguage (AimetaNative) error:', e);
    }
    return {};
  },
  // 获取本地配置信息
  async getLocalConfig() {
    try {
      const jsManager = getWebView2HostObjects()?.JsManager;
      if (jsManager && typeof jsManager.getCheckPointText === 'function') {
        const result = await jsManager.getCheckPointText();
        const parsed =
          typeof result === 'string'
            ? JSON.parse(result || '{}')
            : result || {};
        const normalized = normalizeCheckpointConfig(parsed);
        console.log('[checkpoint] getLocalConfig raw:', result);
        console.log('[checkpoint] getLocalConfig normalized:', normalized);
        return normalized;
      }
    } catch (e) {
      console.warn('getLocalConfig error:', e);
    }
    return {};
  },

  async getServiceConfig() {
    try {
      const jsManager = getWebViewHostObjects()?.JsManager;
      if (jsManager && typeof jsManager.getConfig === 'function') {
        const result = await jsManager.getConfig();
        return JSON.parse(result || '{}');
      }
    } catch (e) {
      console.warn('getServiceConfig error:', e);
    }
    return {};
  },
  async openDirectoryByPath(path: string) {
    try {
      const jsManager = getWebView2HostObjects()?.JsManager;
      if (jsManager && typeof jsManager.OpenDirectoryByPath === 'function') {
        const result = await jsManager.OpenDirectoryByPath(path);
        if (typeof result === 'string') {
          return JSON.parse(result || '{}');
        }
        return result || {};
      }
    } catch (e) {
      console.warn('openDirectoryByPath error:', e);
    }
    return { code: 1, message: 'OpenDirectoryByPath host object unavailable' };
  },

  /** 本机 TTS 端口是否已监听（与 JsManager.IsTtsPreviewPortOpen 一致） */
  async isTtsPreviewPortOpen() {
    if (liveRoomSettingsHostSuspended) {
      return {
        code: 1,
        message: 'host suspended',
        data: null as { open?: boolean; ttsPort?: number } | null,
      };
    }
    const host = getWebView2HostObjects()?.JsManager as
      | { IsTtsPreviewPortOpen?: () => Promise<string> | string }
      | undefined;
    if (!host?.IsTtsPreviewPortOpen) {
      return {
        code: 1,
        message: 'IsTtsPreviewPortOpen unavailable',
        data: null,
      };
    }
    try {
      const raw = await Promise.resolve(host.IsTtsPreviewPortOpen());
      if (typeof raw === 'string') {
        return JSON.parse(raw || '{}') as {
          code: number;
          message?: string;
          data?: { open?: boolean; ttsPort?: number };
        };
      }
      if (raw && typeof raw === 'object' && 'code' in (raw as object)) {
        return raw as {
          code: number;
          message?: string;
          data?: { open?: boolean; ttsPort?: number };
        };
      }
    } catch (e) {
      return { code: 1, message: String(e), data: null };
    }
    return {
      code: 1,
      message: 'Invalid IsTtsPreviewPortOpen response',
      data: null,
    };
  },

  /**
   * 试听前确保本机 TTS 已启动（与 JsManager.EnsureTtsRunningForPreview 一致）。
   * 入参：speechDesign、voicePathName
   */
  async ensureTtsRunningForPreview(payload: {
    speechDesign: number;
    voicePathName: string;
  }) {
    if (liveRoomSettingsHostSuspended) {
      return { code: 1, message: 'host suspended', data: null };
    }
    const host = getWebView2HostObjects()?.JsManager as
      | {
        EnsureTtsRunningForPreview?: (
          json: string
        ) => Promise<string> | string;
      }
      | undefined;
    if (!host?.EnsureTtsRunningForPreview) {
      return {
        code: 1,
        message: 'EnsureTtsRunningForPreview unavailable',
        data: null,
      };
    }
    try {
      const raw = await Promise.resolve(
        host.EnsureTtsRunningForPreview(JSON.stringify(payload))
      );
      if (typeof raw === 'string') {
        return JSON.parse(raw || '{}') as {
          code: number;
          message?: string;
          data?: {
            open?: boolean;
            alreadyRunning?: boolean;
            launched?: boolean;
            ttsPort?: number;
          };
        };
      }
      if (raw && typeof raw === 'object' && 'code' in (raw as object)) {
        return raw as {
          code: number;
          message?: string;
          data?: {
            open?: boolean;
            alreadyRunning?: boolean;
            launched?: boolean;
            ttsPort?: number;
          };
        };
      }
    } catch (e) {
      return { code: 1, message: String(e), data: null };
    }
    return {
      code: 1,
      message: 'Invalid EnsureTtsRunningForPreview response',
      data: null,
    };
  },

  /**
   * 数字人预览：Talking 就绪后向本机 WS 发送 exec（与 JsManager.SendTalkingPreviewExec 一致）。
   * text 与 audioFileBaseName 二选一；音频文件须在 Data/AudioFiles 下 .mp3/.wav。
   */
  async sendTalkingPreviewExec(
    payload:
      | {
        text?: string;
        speed: number;
        voicePathName: string;
        audioFileBaseName?: string;
      }
      | string
  ) {
    if (liveRoomSettingsHostSuspended) {
      return { code: 1, message: 'host suspended', data: null };
    }
    const host = getWebView2HostObjects()?.JsManager as
      | { SendTalkingPreviewExec?: (json: string) => Promise<string> | string }
      | undefined;
    if (!host?.SendTalkingPreviewExec) {
      return {
        code: 1,
        message: 'SendTalkingPreviewExec unavailable',
        data: null,
      };
    }
    try {
      const json =
        typeof payload === 'string' ? payload : JSON.stringify(payload);
      const raw = await Promise.resolve(host.SendTalkingPreviewExec(json));
      if (typeof raw === 'string') {
        return JSON.parse(raw || '{}') as {
          code: number;
          message?: string;
          data?: unknown;
        };
      }
      if (raw && typeof raw === 'object' && 'code' in (raw as object)) {
        return raw as { code: number; message?: string; data?: unknown };
      }
    } catch (e) {
      return { code: 1, message: String(e), data: null };
    }
    return {
      code: 1,
      message: 'Invalid SendTalkingPreviewExec response',
      data: null,
    };
  },

  /**
   * 自由话术文本模式试听：需先保证端口已监听（EnsureTtsRunningForPreview）；POST /tts 流式 PCM 由 NAudio 播放。
   * payload 与 C# PreviewFreeScriptTts 一致：text, speechDesign, voicePathName, speed, language
   */
  async previewFreeScriptTts(payload: {
    text: string;
    speechDesign: number;
    voicePathName: string;
    speed: number;
    language: number;
  }) {
    if (liveRoomSettingsHostSuspended) {
      return { code: 1, message: 'host suspended', data: null };
    }
    const host = getWebView2HostObjects()?.JsManager as
      | { PreviewFreeScriptTts?: (json: string) => Promise<string> | string }
      | undefined;
    if (!host?.PreviewFreeScriptTts) {
      return {
        code: 1,
        message: 'PreviewFreeScriptTts unavailable',
        data: null,
      };
    }
    try {
      const raw = await Promise.resolve(
        host.PreviewFreeScriptTts(JSON.stringify(payload))
      );
      if (typeof raw === 'string') {
        return JSON.parse(raw || '{}') as {
          code: number;
          message?: string;
          data?: unknown;
        };
      }
      if (raw && typeof raw === 'object' && 'code' in (raw as object)) {
        return raw as { code: number; message?: string; data?: unknown };
      }
    } catch (e) {
      return { code: 1, message: String(e), data: null };
    }
    return {
      code: 1,
      message: 'Invalid PreviewFreeScriptTts response',
      data: null,
    };
  },
};

// 导出类型
export type LocalMethods = typeof methods;

// Vue插件安装
export const globalMethodsPlugin = {
  install(app: App) {
    // 方式1：挂载到全局属性（选项式API可用）
    // app.config.globalProperties.$lm = methods;

    // 方式2：提供依赖注入（组合式API推荐）
    app.provide('localMethods', methods);
  },
};

// 默认导出方法集合（适合直接导入使用）
export default methods;
